import { useState, useRef } from 'react';
import { RefreshCw } from 'lucide-react';

// A reusable pull-to-refresh wrapper for mobile touch screens.
// Wrap any scrollable screen's content in <PullToRefresh onRefresh={fn}>...</PullToRefresh>
// and it handles the pull gesture, visual indicator, and calling your refresh function.
//
// How it works: tracks the finger's vertical movement ONLY when the page
// is already scrolled to the very top (pulling down further would otherwise
// do nothing) — this mimics the native pull-to-refresh feel on iOS/Android.
function PullToRefresh({ onRefresh, children }) {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const isPulling = useRef(false);

  const PULL_THRESHOLD = 70; // how far (px) the user must pull before refresh triggers
  const MAX_PULL = 100; // caps how far the indicator visually travels

  const handleTouchStart = (e) => {
    // Only start tracking a pull if the page is scrolled all the way to the top —
    // otherwise this would interfere with normal scrolling.
    if (window.scrollY === 0 && !refreshing) {
      startY.current = e.touches[0].clientY;
      isPulling.current = true;
    }
  };

  const handleTouchMove = (e) => {
    if (!isPulling.current) return;

    const currentY = e.touches[0].clientY;
    const distance = currentY - startY.current;

    if (distance > 0) {
      setPullDistance(Math.min(distance, MAX_PULL));
    }
  };

  const handleTouchEnd = async () => {
    if (!isPulling.current) return;
    isPulling.current = false;

    if (pullDistance >= PULL_THRESHOLD) {
      setRefreshing(true);
      setPullDistance(PULL_THRESHOLD); // hold the indicator in place while refreshing
      await onRefresh();
      setRefreshing(false);
    }

    setPullDistance(0);
  };

  // Spinner rotates proportionally to how far the user has pulled,
  // then spins continuously once refreshing actually starts.
  const spinnerRotation = refreshing ? undefined : `${(pullDistance / PULL_THRESHOLD) * 360}deg`;

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull indicator — grows/shrinks with pullDistance, spins during refresh */}
      <div
        style={{
          height: pullDistance,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          transition: isPulling.current ? 'none' : 'height 0.2s ease',
        }}
      >
        <RefreshCw
          size={22}
          color="#2563eb"
          style={{
            transform: spinnerRotation ? `rotate(${spinnerRotation})` : undefined,
            animation: refreshing ? 'spin 0.7s linear infinite' : 'none',
          }}
        />
      </div>

      {children}
    </div>
  );
}

export default PullToRefresh;